import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Navbar,
  Page,
  Popup,
  NavRight,
  Link,
  Gauge,
  f7
} from 'framework7-react';
import II18 from '../../../translate/II18';
import { gae } from '../../../helpers/Analytics';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import PaperflyAPI from '../../../../server/api/PaperflyAPI';
import {
  EXTERNAL_POPUP_WINDOW_STATUS,
  prepareExternalPopUpWindow,
  setExternalPopUpWindowStatus,
  setRegistrationData
} from '../../../../redux/actions/rootAction';
import {
  receiveESignatureData,
  requestDocumentESignature,
  resetDocumentESignatureStatus,
  setAtrustShortTerm,
  clearAtrustShortTerm
} from '../../../../redux/actions/esignature';
import {
  subscribeWebSocket,
  unSubscribeWebSocket
} from '../../../../redux/actions/websocket';
import {
  openAtrustPopupWin,
  popupDiagLabel,
  redirectExternalPopUp
} from '../utils';
import { startCronJob, stopCronJob } from '../../../../redux/actions/cronJobs';
import { atrustRegisterFormStructure } from './atrustRegisterFormStructure';
import { findAllFormErrors } from '../../../helpers/formUtils';
import AtrustOption from './AtrustOption';
import RegistrationForm from '../RegistrationForm';
import { useRegistrationData } from '../useRegistrationData';
import { alertDialog, confirmDialog } from '../../../helpers/dialogUtils';
import QesStepList, { QES_STEP_STATUS } from '../QesStepList';
import QesSuccessFooter from '../QesSuccessFooter';
import QesCooldownFillButton from '../QesCooldownFillButton';
import { QES_STEP_ID, atrustProvider } from '../providers';
import { createSignatureStatusWatch } from '../signatureStatusWatch';
import {
  isUserCredentialsProcessing,
  isUserCredentialsEmbedInCaller
} from '../../../../redux/userCredentialsTask';
import { isCreateTemplatePreparing } from '../../../../redux/createTemplateRequestTask';
import UserCredentialsStep from '../UserCredentialsStep';

const REGISTER_ATRUST_JOB = 'REGISTER_ATRUST_JOB';
const ATRUST_ACTIONS = {
  LOGIN: 'LOGIN',
  REGISTER: 'REGISTER'
};

const PREP_PURPOSE = {
  LOGIN: 'login',
  REGISTER: 'register'
};

/** Short-term cert window after WebID when activationWorkflow is "2". */
const ATRUST_SHORT_TERM_MS = 15 * 60 * 1000;

const isAtrustShortTermWorkflow = activationWorkflow =>
  String(activationWorkflow) === '2';

/** registration/get lastModified → epoch ms (ISO string, ms, or seconds). */
const parseAtrustLastModifiedMs = value => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value < 1e12 ? value * 1000 : value;
  }
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

/** Short-term deadline from lastModified, or now if field missing (fresh WebID OK). */
const shortTermDeadlineAt = (lastModified, now = Date.now()) => {
  const startMs = parseAtrustLastModifiedMs(lastModified);
  return (startMs != null ? startMs : now) + ATRUST_SHORT_TERM_MS;
};

const formatShortTermMmSs = ms => {
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const emptyStepMap = () => ({
  [QES_STEP_ID.CREATE_REQUEST]: null,
  [QES_STEP_ID.PREPARE_DOCUMENT]: null,
  [QES_STEP_ID.CHECK_REGISTRATION]: null,
  [QES_STEP_ID.COMPLETE_REGISTRATION]: null,
  [QES_STEP_ID.COMPLETE_SIGNING]: null,
  [QES_STEP_ID.SIGNED_SUCCESS]: null
});

export default function AtrustRegisterPopUp(props) {
  const {
    opened,
    noanimate,
    docId,
    signatureFieldId,
    documentIsReadyToSign,
    prepareDocumentToSign,
    prepareDocumentToSignError,
    currentRecipientData,
    templateMode,
    documentIsQualifiedSigned,
    onSignFlowDone
  } = props;

  const dispatch = useDispatch();

  const { registrationData } = useRegistrationData(
    signatureFieldId,
    atrustRegisterFormStructure,
    currentRecipientData
  );

  const recipientRequestId = useSelector(state => state.recipientRequestId);
  const atrustShortTerm = useSelector(state => state.atrustShortTerm);
  const externalPopUpStatus = useSelector(state => state.externalPopUpStatus);
  const externalPopUpOptions = useSelector(state => state.externalPopUpOptions);
  const userCredentialsTask = useSelector(state => state.userCredentialsTask);
  const createTemplateRequestTask = useSelector(
    state => state.createTemplateRequestTask
  );
  const atrustShortTermRef = useRef(atrustShortTerm);
  atrustShortTermRef.current = atrustShortTerm;
  const recipientRequestIdRef = useRef(recipientRequestId);
  recipientRequestIdRef.current = recipientRequestId;
  const credentialsActive = isUserCredentialsProcessing(userCredentialsTask);
  const embedCredentials =
    credentialsActive && isUserCredentialsEmbedInCaller(userCredentialsTask);
  const credsStepRef = useRef(null);
  const [credsValid, setCredsValid] = useState(false);
  const extStatusRef = useRef(externalPopUpStatus);
  extStatusRef.current = externalPopUpStatus;
  const extOptionsRef = useRef(externalPopUpOptions);
  extOptionsRef.current = externalPopUpOptions;
  const signRequestRef = useRef(null);
  const signApiInFlightRef = useRef(false);
  const signWatchRef = useRef(null);
  const skipRestoreOnCloseRef = useRef(false);
  /** Bumped on pause/cancel and each WebID start — ignore stale getAtrustWebIdStart. */
  const webIdStartEpochRef = useRef(0);
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;

  const [atrustNextAction, setAtrustNextAction] = useState('');
  const [errorOnRegistration, setErrorOnRegistration] = useState('');
  const [showRegistration, setShowRegistration] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [atrustRegistrationUrl, setAtrustRegistrationUrl] = useState('');
  const [
    waitForDocumentIsReadyToSign,
    setWaitForDocumentIsReadyToSign
  ] = useState(false);
  const [showPreparing, setShowPreparing] = useState(false);
  const [prepPurpose, setPrepPurpose] = useState(null);
  const [stepStatus, setStepStatus] = useState(emptyStepMap);
  const [readyForPrepStart, setReadyForPrepStart] = useState(false);
  const [startingCta, setStartingCta] = useState(false);
  const [awaitExtPopupReady, setAwaitExtPopupReady] = useState(null);
  /** External WebID window closed; keep registration/get poll until terminal. */
  const [registrationStatusChecking, setRegistrationStatusChecking] = useState(
    false
  );
  const [restartCooldown, setRestartCooldown] = useState(false);
  const [phase, setPhase] = useState('choice');
  /** After A-Trust sign window closed early — short-term CTA becomes Repeat. */
  const [signRepeatNeeded, setSignRepeatNeeded] = useState(false);
  const [shortTermNow, setShortTermNow] = useState(() => Date.now());
  const prevExtPopUpStatusRef = useRef(externalPopUpStatus);
  const prevTemplateModeRef = useRef(templateMode);
  const signSuccessHandledRef = useRef(false);
  const stepStatusRef = useRef(stepStatus);
  stepStatusRef.current = stepStatus;

  const shortTermForRequest =
    atrustShortTerm &&
    atrustShortTerm.recipientRequestId === recipientRequestId &&
    isAtrustShortTermWorkflow(atrustShortTerm.activationWorkflow)
      ? atrustShortTerm
      : null;
  const shortTermRemainingMs = shortTermForRequest
    ? shortTermForRequest.deadlineAt - shortTermNow
    : null;
  const shortTermExpired = Boolean(
    shortTermForRequest && shortTermRemainingMs <= 0
  );

  // Restart CTA: 5s fill cooldown (WebID status-check or Repeat Document Sign).
  useEffect(() => {
    if (!registrationStatusChecking && !signRepeatNeeded) {
      setRestartCooldown(false);
      return undefined;
    }
    setRestartCooldown(true);
    const t = setTimeout(() => setRestartCooldown(false), 5000);
    return () => clearTimeout(t);
  }, [registrationStatusChecking, signRepeatNeeded]);

  // Wall-clock tick for short-term countdown (deadline lives in Redux).
  useEffect(() => {
    if (!opened || !shortTermForRequest) {
      return undefined;
    }
    setShortTermNow(Date.now());
    const t = setInterval(() => setShortTermNow(Date.now()), 1000);
    return () => clearInterval(t);
    // Intentionally key off deadline fields, not the whole shortTermForRequest object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opened,
    shortTermForRequest?.deadlineAt,
    shortTermForRequest?.recipientRequestId
  ]);

  const setStep = (id, status) => {
    setStepStatus(prev => ({ ...prev, [id]: status }));
  };

  const resetPreparingState = () => {
    signWatchRef.current?.pause();
    signRequestRef.current = null;
    signApiInFlightRef.current = false;
    setShowPreparing(false);
    setPrepPurpose(null);
    setReadyForPrepStart(false);
    setStartingCta(false);
    setAwaitExtPopupReady(null);
    setRegistrationStatusChecking(false);
    setWaitForDocumentIsReadyToSign(false);
    setStepStatus(emptyStepMap());
    setPhase('choice');
    signSuccessHandledRef.current = false;
  };

  const buildWebIdRecipientData = () =>
    atrustRegisterFormStructure
      .filter(item => !item.static)
      .reduce(
        (acc, item) => ({
          ...acc,
          [item.id]:
            item.id === 'countryCode'
              ? atrustRegisterFormStructure
                  .find(i => i.id === 'countryCode')
                  .options.find(o => o.id === registrationData.get(item.id)[0])
                  ?.customId
              : item.id === 'dateOfBirth'
              ? new Date(registrationData.get(item.id)).getTime()
              : registrationData.get(item.id)
        }),
        {}
      );

  const showSigningAwaitCta = () => {
    setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.AWAITING_USER);
    setReadyForPrepStart(true);
    setStartingCta(false);
    setPhase('preparing');
    setShowPreparing(true);
  };

  const finishLoginPrepReady = () => {
    const ss = stepStatusRef.current;
    if (ss[QES_STEP_ID.PREPARE_DOCUMENT] != null) {
      setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.DONE);
    }
    if (ss[QES_STEP_ID.CREATE_REQUEST] === QES_STEP_STATUS.RUNNING) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.DONE);
    }
    showSigningAwaitCta();
    gae('ATRUST_REGISTER_POP_UP', 'READY_FOR_LOGIN_START');
  };

  const continueAfterDocumentReady = (purpose = prepPurpose) => {
    const ss = stepStatusRef.current;
    if (ss[QES_STEP_ID.CREATE_REQUEST] != null) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.DONE);
    }
    if (ss[QES_STEP_ID.PREPARE_DOCUMENT] != null) {
      setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.DONE);
    }

    if (purpose === PREP_PURPOSE.LOGIN) {
      finishLoginPrepReady();
      return;
    }

    setStep(QES_STEP_ID.CHECK_REGISTRATION, QES_STEP_STATUS.RUNNING);

    PaperflyAPI.getAtrustRegisterStatus(recipientRequestId).then(
      response => {
        if (response.state === 'OK') {
          gae('ATRUST_REGISTER_POP_UP', 'CANT_REGISTER', 'ACCOUNT_REGISTERED');
          // Account exists — no confirm dialog; branch on short-term window.
          setStep(QES_STEP_ID.CHECK_REGISTRATION, QES_STEP_STATUS.DONE);

          const activationWorkflow = response.activationWorkflow;
          if (isAtrustShortTermWorkflow(activationWorkflow)) {
            const lastModifiedMs = parseAtrustLastModifiedMs(
              response.lastModified
            );
            const deadlineAt =
              lastModifiedMs != null
                ? lastModifiedMs + ATRUST_SHORT_TERM_MS
                : null;
            if (deadlineAt != null && deadlineAt > Date.now()) {
              gae(
                'ATRUST_REGISTER_POP_UP',
                'ALREADY_REGISTERED_SHORT_TERM_RESTORE'
              );
              dispatch(
                setAtrustShortTerm({
                  recipientRequestId,
                  activationWorkflow,
                  deadlineAt
                })
              );
              setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.DONE);
              showSigningAwaitCta();
              return;
            }
            gae(
              'ATRUST_REGISTER_POP_UP',
              'ALREADY_REGISTERED_SHORT_TERM_EXPIRED',
              lastModifiedMs == null ? 'NO_LAST_MODIFIED' : 'WEBID_START'
            );
            if (
              atrustShortTermRef.current?.recipientRequestId ===
              recipientRequestId
            ) {
              dispatch(clearAtrustShortTerm());
            }
            setStep(
              QES_STEP_ID.COMPLETE_REGISTRATION,
              QES_STEP_STATUS.AWAITING_USER
            );
            setReadyForPrepStart(true);
            setStartingCta(false);
            return;
          }

          // Classic / long-term account — go straight to sign Start.
          gae('ATRUST_REGISTER_POP_UP', 'ALREADY_REGISTERED_CLASSIC_SIGN');
          if (
            atrustShortTermRef.current?.recipientRequestId ===
            recipientRequestId
          ) {
            dispatch(clearAtrustShortTerm());
          }
          setStep(QES_STEP_ID.COMPLETE_REGISTRATION, null);
          showSigningAwaitCta();
          return;
        }

        // Need WebID — keep check_registration out of the step list.
        setStep(QES_STEP_ID.CHECK_REGISTRATION, null);
        setStep(
          QES_STEP_ID.COMPLETE_REGISTRATION,
          QES_STEP_STATUS.AWAITING_USER
        );
        setReadyForPrepStart(true);
        setStartingCta(false);
        gae('ATRUST_REGISTER_POP_UP', 'READY_FOR_WEBID_START');
      },
      () => {
        setStep(QES_STEP_ID.CHECK_REGISTRATION, null);
        setStep(
          QES_STEP_ID.COMPLETE_REGISTRATION,
          QES_STEP_STATUS.AWAITING_USER
        );
        setReadyForPrepStart(true);
        setStartingCta(false);
        gae(
          'ATRUST_REGISTER_POP_UP',
          'READY_FOR_WEBID_START',
          'STATUS_SKIPPED'
        );
      }
    );
  };

  const beginDocumentPrep = purpose => {
    setErrorOnRegistration('');
    setReadyForPrepStart(false);
    setStartingCta(false);
    setPrepPurpose(purpose);
    setShowPreparing(true);
    setPhase('preparing');

    // Only current prep steps — later steps appear when previous finish.
    const next = emptyStepMap();
    if (templateMode) {
      next[QES_STEP_ID.CREATE_REQUEST] = QES_STEP_STATUS.RUNNING;
    } else if (!documentIsReadyToSign) {
      next[QES_STEP_ID.PREPARE_DOCUMENT] = QES_STEP_STATUS.RUNNING;
    } else {
      next[QES_STEP_ID.PREPARE_DOCUMENT] = QES_STEP_STATUS.DONE;
    }
    setStepStatus(next);

    if (!documentIsReadyToSign) {
      setWaitForDocumentIsReadyToSign(true);
      prepareDocumentToSign();
      return;
    }

    continueAfterDocumentReady(purpose);
  };

  const beginLoginPrep = () => {
    gae('ATRUST_REGISTER_POP_UP', 'START_LOGIN_PREP');
    if (documentIsReadyToSign) {
      setPrepPurpose(PREP_PURPOSE.LOGIN);
      setShowPreparing(true);
      setPhase('preparing');
      setStepStatus({
        ...emptyStepMap(),
        [QES_STEP_ID.COMPLETE_SIGNING]: QES_STEP_STATUS.AWAITING_USER
      });
      setReadyForPrepStart(true);
      return;
    }
    beginDocumentPrep(PREP_PURPOSE.LOGIN);
  };

  const beginRegistrationPrep = () => {
    const errors = findAllFormErrors(
      [atrustRegisterFormStructure],
      registrationData
    );

    if (errors.size > 0) {
      gae('ATRUST_REGISTER_POP_UP', 'CANT_START_REGISTRATION', 'FORM_ERRORS');
      setShowFormErrors(true);
      alertDialog(II18.get('dialog_fill_all_required'));
      return;
    }

    gae('ATRUST_REGISTER_POP_UP', 'START_REGISTRATION_PREP');
    dispatch(setRegistrationData(registrationData));
    beginDocumentPrep(PREP_PURPOSE.REGISTER);
  };

  const onChoiceNext = () => {
    if (atrustNextAction === ATRUST_ACTIONS.LOGIN) {
      beginLoginPrep();
      return;
    }
    setShowRegistration(true);
    setPhase('form');
  };

  const preparePopUpWindow = () => {
    if (externalPopUpStatus !== EXTERNAL_POPUP_WINDOW_STATUS.OPEN) {
      gae('ATRUST_REGISTER_BROWSER_POP_UP', 'PREPARE_EXT_POPUP');
      const winInfo = openAtrustPopupWin();
      dispatch(prepareExternalPopUpWindow(winInfo));
      return winInfo;
    }
    return {
      winReference: externalPopUpOptions?.winReference
    };
  };

  const markPopupBlocked = stepId => {
    setStep(stepId, QES_STEP_STATUS.BLOCKED);
    setStartingCta(false);
    setAwaitExtPopupReady(null);
    setReadyForPrepStart(true);
  };

  /** Stop registration status cron without forcing another CLOSE (window may already be gone). */
  const pauseRegistrationPolling = () => {
    webIdStartEpochRef.current += 1;
    setRegistrationStatusChecking(false);
    dispatch(stopCronJob(REGISTER_ATRUST_JOB));
    f7.dialog.close();
  };

  const isRegistrationPollActive = () =>
    registrationStatusChecking ||
    stepStatusRef.current[QES_STEP_ID.COMPLETE_REGISTRATION] ===
      QES_STEP_STATUS.RUNNING;

  const confirmAbortStatusCheck = onConfirm => {
    confirmDialog(
      II18.get('title_confirmation'),
      II18.get('dialog_atrust_confirm_abort_status_check'),
      onConfirm,
      undefined,
      II18.get('title_yes'),
      II18.get('title_no')
    );
  };

  const closeNamedExtPopupSync = reason => {
    const win = extOptionsRef.current?.winReference;
    if (win && !win.closed) {
      gae(
        'EXTERNAL_POPUP',
        'PWA_CLOSE_SYNC',
        popupDiagLabel(win, `reason=${reason || 'unspecified'}`)
      );
      try {
        win.close();
      } catch (e) {
        // ignore
      }
    }
  };

  const stopCheckingRegistration = () => {
    pauseRegistrationPolling();
    closeNamedExtPopupSync('stop_registration_check');
    dispatch(setExternalPopUpWindowStatus(EXTERNAL_POPUP_WINDOW_STATUS.CLOSE));
  };

  const isLiveShortTerm = () => {
    const st = atrustShortTermRef.current;
    return Boolean(
      st &&
        st.recipientRequestId === recipientRequestIdRef.current &&
        isAtrustShortTermWorkflow(st.activationWorkflow) &&
        st.deadlineAt > Date.now()
    );
  };

  /** User closed provider popup — back to hourglass + START, not blocked.
   *  Short-term keeps the signature watch and starts HTTP poll now: pause()
   *  would cancel the 30s delayed cron, so status-check would never poll.
   */
  const restoreAwaitingUser = stepId => {
    gae('ATRUST_REGISTER_POP_UP', 'EXT_POPUP_CLOSED_RESTORE_AWAIT', stepId);
    if (stepId === QES_STEP_ID.COMPLETE_SIGNING) {
      // Closed IdP session invalidates the auth URL; next Start must
      // call pwasigndocuments again (fresh url). The watch keeps the
      // in-flight process id for status polling.
      signRequestRef.current = null;
      setSignRepeatNeeded(true);
      if (isLiveShortTerm()) {
        gae('ATRUST_REGISTER_POP_UP', 'SHORT_TERM_SIGN_STATUS_CHECK');
        signWatchRef.current?.ensurePolling?.();
      } else {
        signWatchRef.current?.pause();
      }
    } else if (stepId === QES_STEP_ID.COMPLETE_REGISTRATION) {
      pauseRegistrationPolling();
      // Drop URL so a late getAtrustWebIdStart cannot redirect a future popup.
      setAtrustRegistrationUrl('');
    }
    setStep(stepId, QES_STEP_STATUS.AWAITING_USER);
    setStartingCta(false);
    setAwaitExtPopupReady(null);
    setReadyForPrepStart(true);
    setPhase('preparing');
    setShowPreparing(true);
  };

  const fatalSignError = err => {
    gae('ESIGN_PROCESS', 'ERROR', err);
    signWatchRef.current?.pause();
    signApiInFlightRef.current = false;
    dispatch(setExternalPopUpWindowStatus(EXTERNAL_POPUP_WINDOW_STATUS.CLOSE));
    f7.dialog.alert(
      II18.get('esignature_sign_document_error_message', err),
      II18.get('esignature_sign_document_error_title')
    );
    onCloseRef.current?.();
  };

  const submitSignRequest = req => {
    const win = extOptionsRef.current?.winReference;
    gae('ESIGN_PROCESS', 'SUBMIT_DATA_TO_EXT_POPUP', popupDiagLabel(win));
    dispatch(requestDocumentESignature(docId, req.internalCallbackId));
    atrustProvider.submitToPopup(win, req.url, 'sign');
    // Short-term: do not wait 30s. The A-Trust window often closes first, and
    // if the websocket misses the result there is still no status check.
    signWatchRef.current?.start(
      req.internalCallbackId,
      atrustProvider,
      isLiveShortTerm() ? 0 : undefined
    );
  };

  const createAtrustSignProcess = () => {
    if (signApiInFlightRef.current) {
      gae('ESIGN_PROCESS', 'CREATE_ATRUST_SIGNATURE_PROCESS', 'SKIP_DUPLICATE');
      return;
    }
    signApiInFlightRef.current = true;
    gae('ESIGN_PROCESS', 'CREATE_ATRUST_SIGNATURE_PROCESS');
    dispatch(resetDocumentESignatureStatus(docId));
    PaperflyAPI.atrustCreateSignatureProcess(recipientRequestId, [
      {
        documentId: docId,
        widgetId: signatureFieldId
      }
    ]).then(
      response => {
        signApiInFlightRef.current = false;
        if (response.error) {
          gae('ESIGN_PROCESS', 'ERROR_ON_CREATE_ATRUST_SIGNATURE_PROCESS');
          fatalSignError(response.error);
          return;
        }
        gae('ESIGN_PROCESS', 'CREATE_ATRUST_SIGNATURE_PROCESS_SUCCESS');
        const req = {
          url: response.url,
          internalCallbackId: response.internalCallbackId
        };
        signRequestRef.current = req;
        submitSignRequest(req);
      },
      error => {
        signApiInFlightRef.current = false;
        fatalSignError(
          error.response?.data?.error || error.response?.data?.errorCode
        );
      }
    );
  };

  const runSignAfterPopupReady = () => {
    setSignRepeatNeeded(false);
    setAwaitExtPopupReady(null);
    const existing = signRequestRef.current;
    if (existing?.url) {
      gae('ESIGN_PROCESS', 'EXT_POPUP_READY', 'REUSE_EXISTING');
      submitSignRequest(existing);
      return;
    }
    createAtrustSignProcess();
  };

  const checkAtrustAccount = () => {
    PaperflyAPI.getAtrustRegisterStatus(recipientRequestId).then(response => {
      if (response.state === 'OK') {
        setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.DONE);
        // Stale WebID URL must not redirect the later sign popup (OPEN effect).
        setAtrustRegistrationUrl('');
        stopCheckingRegistration();
        setSignRepeatNeeded(false);
        const activationWorkflow = response.activationWorkflow;
        if (isAtrustShortTermWorkflow(activationWorkflow)) {
          dispatch(
            setAtrustShortTerm({
              recipientRequestId,
              activationWorkflow,
              deadlineAt: shortTermDeadlineAt(response.lastModified)
            })
          );
        } else if (
          atrustShortTermRef.current?.recipientRequestId === recipientRequestId
        ) {
          dispatch(clearAtrustShortTerm());
        }
        showSigningAwaitCta();
      } else if (response.state === 'FAILED') {
        setErrorOnRegistration(II18.get('dialog_atrust_error_on_registration'));
        stopCheckingRegistration();
        setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.ERROR);
      } else if (response.state === 'CANCEL') {
        setErrorOnRegistration(
          II18.get('dialog_atrust_cancelled_on_registration')
        );
        stopCheckingRegistration();
        setStep(
          QES_STEP_ID.COMPLETE_REGISTRATION,
          QES_STEP_STATUS.AWAITING_USER
        );
        setReadyForPrepStart(true);
      } else if (response.state && response.state !== 'CREATED') {
        setErrorOnRegistration(
          II18.get(
            'dialog_atrust_unknown_error_on_registration',
            response.state
          )
        );
        stopCheckingRegistration();
        setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.ERROR);
      }
    });
  };

  const startCheckingRegistration = () => {
    gae('ATRUST_REGISTER_POP_UP', 'START_REGISTRATION_CRONJOB');
    dispatch(startCronJob(REGISTER_ATRUST_JOB, 1000, checkAtrustAccount));
  };

  const runWebIdStartAfterPopupReady = () => {
    setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.RUNNING);
    const epoch = ++webIdStartEpochRef.current;

    const recipientData = buildWebIdRecipientData();
    PaperflyAPI.getAtrustWebIdStart(recipientRequestId, recipientData).then(
      response => {
        // Popup closed / cancelled while API was in flight — do not restart cron.
        if (epoch !== webIdStartEpochRef.current) {
          gae('ATRUST_REGISTER_POP_UP', 'WEBID_START_STALE');
          return;
        }
        setStartingCta(false);
        if (response.error) {
          gae('ATRUST_REGISTER_API', 'ERROR', response.error);
          setErrorOnRegistration(response.error);
          setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.ERROR);
          setShowRegistration(true);
          setPhase('form');
          setShowPreparing(false);
          return;
        }
        setAtrustRegistrationUrl(response.url);
        startCheckingRegistration();
      },
      error => {
        if (epoch !== webIdStartEpochRef.current) {
          gae('ATRUST_REGISTER_POP_UP', 'WEBID_START_STALE');
          return;
        }
        gae('ATRUST_REGISTER_API', 'ERROR', error.response?.data?.error);
        setStartingCta(false);
        setErrorOnRegistration(error.response?.data?.error);
        setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.ERROR);
        setShowRegistration(true);
        setPhase('form');
        setShowPreparing(false);
      }
    );
  };

  const startWebIdUnderGesture = () => {
    gae('ATRUST_REGISTER_POP_UP', 'USER_CLICK_START_WEBID');
    setRegistrationStatusChecking(false);
    setStartingCta(true);
    setErrorOnRegistration('');
    setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.RUNNING);
    setAwaitExtPopupReady(null);

    const winInfo = preparePopUpWindow();
    const win = winInfo?.winReference;
    if (!win || win.closed || typeof win.closed === 'undefined') {
      markPopupBlocked(QES_STEP_ID.COMPLETE_REGISTRATION);
      gae('ATRUST_REGISTER_BROWSER_POP_UP', 'BLOCKED_ON_START');
      return;
    }

    if (externalPopUpStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN) {
      runWebIdStartAfterPopupReady();
      return;
    }
    setAwaitExtPopupReady('webid');
  };

  const startSignUnderGesture = () => {
    gae('ATRUST_REGISTER_POP_UP', 'USER_CLICK_START_SIGN');
    setStartingCta(true);
    setErrorOnRegistration('');
    // Sign popup must stay on loading.html until pwasigndocuments URL arrives.
    setAtrustRegistrationUrl('');
    setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.RUNNING);
    setAwaitExtPopupReady(null);

    // Close prior WebID/named window so window.open under this gesture is fresh.
    // Only skip the next OPEN→CLOSE restore when we actually close an open window;
    // if already CLOSED, skipRestore would stick and swallow the user's later close.
    const priorWin = extOptionsRef.current?.winReference;
    if (priorWin && !priorWin.closed) {
      skipRestoreOnCloseRef.current = true;
      closeNamedExtPopupSync('before_sign_open');
      dispatch(
        setExternalPopUpWindowStatus(EXTERNAL_POPUP_WINDOW_STATUS.CLOSE)
      );
    }

    gae('ESIGN_PROCESS', 'OPEN_ATRUST_EXT_POPUP');
    const winInfo = openAtrustPopupWin();
    dispatch(prepareExternalPopUpWindow(winInfo));
    const win = winInfo?.winReference;
    if (!win || win.closed || typeof win.closed === 'undefined') {
      skipRestoreOnCloseRef.current = false;
      markPopupBlocked(QES_STEP_ID.COMPLETE_SIGNING);
      gae(
        'ATRUST_REGISTER_BROWSER_POP_UP',
        'BLOCKED_ON_SIGN',
        popupDiagLabel(win)
      );
      return;
    }

    setAwaitExtPopupReady('sign');
  };

  const onPrepStart = () => {
    if (registrationStatusChecking) {
      confirmDialog(
        II18.get('title_confirmation'),
        II18.get('dialog_atrust_confirm_restart_registration'),
        () => {
          gae('ATRUST_REGISTER_POP_UP', 'STATUS_CHECK_RESTART_CONFIRMED');
          pauseRegistrationPolling();
          setAtrustRegistrationUrl('');
          startWebIdUnderGesture();
        },
        undefined,
        II18.get('title_yes'),
        II18.get('title_no')
      );
      return;
    }

    const ss = stepStatusRef.current;
    const regAwait =
      ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.AWAITING_USER ||
      ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.BLOCKED;
    const signAwait =
      ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.AWAITING_USER ||
      ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.BLOCKED;

    // Short-term window expired — restart WebID (new 15 min after next OK).
    if (shortTermExpired && signAwait && !regAwait) {
      gae('ATRUST_REGISTER_POP_UP', 'SHORT_TERM_EXPIRED_RESTART_WEBID');
      setSignRepeatNeeded(false);
      setStep(QES_STEP_ID.COMPLETE_SIGNING, null);
      startWebIdUnderGesture();
      return;
    }

    if (regAwait && prepPurpose === PREP_PURPOSE.REGISTER) {
      startWebIdUnderGesture();
      return;
    }

    if (shortTermForRequest && shortTermExpired) {
      return;
    }

    startSignUnderGesture();
  };

  const doCancelPreparing = () => {
    gae('ATRUST_REGISTER_POP_UP', 'USER_BACK_FROM_PREP');
    stopCheckingRegistration();
    const wasRegister = prepPurpose === PREP_PURPOSE.REGISTER;
    resetPreparingState();
    if (wasRegister) {
      setShowRegistration(true);
      setPhase('form');
    } else {
      setPhase('choice');
    }
  };

  const cancelPreparing = () => {
    if (isRegistrationPollActive()) {
      confirmAbortStatusCheck(doCancelPreparing);
      return;
    }
    doCancelPreparing();
  };

  useEffect(() => {
    if (!showPreparing || !opened) return;
    // Embedded credentials screen owns UI — keep CREATE_REQUEST RUNNING underneath
    if (embedCredentials) {
      return;
    }
    if (
      credentialsActive &&
      stepStatus[QES_STEP_ID.CREATE_REQUEST] === QES_STEP_STATUS.RUNNING
    ) {
      // Non-embed overlay open — wait for user (not spinner)
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.AWAITING_USER);
    } else if (
      !credentialsActive &&
      stepStatus[QES_STEP_ID.CREATE_REQUEST] ===
        QES_STEP_STATUS.AWAITING_USER &&
      templateMode &&
      isCreateTemplatePreparing(createTemplateRequestTask)
    ) {
      // Credentials completed — create request continues
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.RUNNING);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    credentialsActive,
    embedCredentials,
    showPreparing,
    opened,
    createTemplateRequestTask,
    templateMode
  ]);

  useEffect(() => {
    if (!opened || !showPreparing || !waitForDocumentIsReadyToSign) return;
    const wasTemplate = prevTemplateModeRef.current;
    prevTemplateModeRef.current = templateMode;

    if (wasTemplate && !templateMode) {
      setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.DONE);
      if (!documentIsReadyToSign) {
        setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.RUNNING);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateMode]);

  useEffect(() => {
    if (opened && documentIsReadyToSign && waitForDocumentIsReadyToSign) {
      setWaitForDocumentIsReadyToSign(false);
      continueAfterDocumentReady(prepPurpose);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentIsReadyToSign]);

  useEffect(() => {
    if (opened && prepareDocumentToSignError && showPreparing) {
      if (stepStatusRef.current[QES_STEP_ID.CREATE_REQUEST] != null) {
        setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.ERROR);
      }
      setStep(QES_STEP_ID.PREPARE_DOCUMENT, QES_STEP_STATUS.ERROR);
      setWaitForDocumentIsReadyToSign(false);
      setErrorOnRegistration(II18.get('dialog_atrust_prep_document_error'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareDocumentToSignError]);

  useEffect(() => {
    const fatal = () => fatalSignError();
    signWatchRef.current = createSignatureStatusWatch({
      startCronJob: (id, period, doJob) =>
        dispatch(startCronJob(id, period, doJob)),
      stopCronJob: id => dispatch(stopCronJob(id)),
      subscribeWebSocket: (topic, id) =>
        dispatch(subscribeWebSocket(topic, id)),
      unSubscribeWebSocket: topic => dispatch(unSubscribeWebSocket(topic)),
      getExternalPopUpStatus: () => extStatusRef.current,
      receiveESignatureData: data => dispatch(receiveESignatureData(data)),
      onProcessFailed: fatal,
      usePreloaderDialog: false
    });
    return () => signWatchRef.current?.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const prevStatus = prevExtPopUpStatusRef.current;
    prevExtPopUpStatusRef.current = externalPopUpStatus;

    if (
      awaitExtPopupReady === 'webid' &&
      externalPopUpStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN
    ) {
      setAwaitExtPopupReady(null);
      gae('ATRUST_REGISTER_BROWSER_POP_UP', 'READY_AFTER_WAIT');
      runWebIdStartAfterPopupReady();
      return;
    }

    if (
      awaitExtPopupReady === 'sign' &&
      externalPopUpStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN
    ) {
      // Reopen completed — further OPEN→CLOSE is a real user close
      skipRestoreOnCloseRef.current = false;
      gae('ESIGN_PROCESS', 'EXT_POPUP_READY');
      runSignAfterPopupReady();
      return;
    }

    if (externalPopUpStatus !== EXTERNAL_POPUP_WINDOW_STATUS.CLOSE) {
      return;
    }

    // Blocked open (PREPARE → CLOSE): orange warning + retry CTA
    if (prevStatus === EXTERNAL_POPUP_WINDOW_STATUS.PREPARE) {
      if (awaitExtPopupReady === 'webid') {
        gae('ATRUST_REGISTER_BROWSER_POP_UP', 'BLOCKED_WHILE_AWAITING');
        markPopupBlocked(QES_STEP_ID.COMPLETE_REGISTRATION);
      } else if (
        awaitExtPopupReady === 'sign' ||
        stepStatusRef.current[QES_STEP_ID.COMPLETE_SIGNING] ===
          QES_STEP_STATUS.RUNNING ||
        startingCta
      ) {
        gae('ATRUST_REGISTER_BROWSER_POP_UP', 'BLOCKED_ON_SIGN');
        markPopupBlocked(QES_STEP_ID.COMPLETE_SIGNING);
      }
      return;
    }

    // Provider window closed while a step is RUNNING
    if (prevStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN && opened) {
      if (skipRestoreOnCloseRef.current) {
        skipRestoreOnCloseRef.current = false;
        return;
      }
      const ss = stepStatusRef.current;
      if (ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.RUNNING) {
        restoreAwaitingUser(QES_STEP_ID.COMPLETE_SIGNING);
      } else if (
        ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.RUNNING
      ) {
        // Keep registration/get poll — A-Trust may close before status is OK.
        gae('ATRUST_REGISTER_POP_UP', 'EXT_POPUP_CLOSED_STATUS_CHECK');
        setAtrustRegistrationUrl('');
        setRegistrationStatusChecking(true);
        setStartingCta(false);
        setAwaitExtPopupReady(null);
        setReadyForPrepStart(true);
        setPhase('preparing');
        setShowPreparing(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitExtPopupReady, externalPopUpStatus, startingCta, opened]);

  useEffect(() => {
    // Only for WebID registration — never reuse this URL on the sign popup.
    if (
      opened &&
      externalPopUpStatus === EXTERNAL_POPUP_WINDOW_STATUS.OPEN &&
      atrustRegistrationUrl &&
      stepStatusRef.current[QES_STEP_ID.COMPLETE_REGISTRATION] ===
        QES_STEP_STATUS.RUNNING
    ) {
      redirectExternalPopUp(
        externalPopUpOptions.winReference,
        atrustRegistrationUrl,
        'webid'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPopUpStatus, atrustRegistrationUrl]);

  useEffect(() => {
    if (
      opened &&
      documentIsQualifiedSigned &&
      !signSuccessHandledRef.current &&
      showPreparing
    ) {
      signSuccessHandledRef.current = true;
      signWatchRef.current?.pause();
      setStep(QES_STEP_ID.COMPLETE_SIGNING, QES_STEP_STATUS.DONE);
      setStep(QES_STEP_ID.SIGNED_SUCCESS, QES_STEP_STATUS.DONE);
      setPhase('success');
      setReadyForPrepStart(false);
      setStartingCta(false);
      setSignRepeatNeeded(false);
      if (
        atrustShortTermRef.current?.recipientRequestId ===
        recipientRequestIdRef.current
      ) {
        dispatch(clearAtrustShortTerm());
      }
    }
  }, [documentIsQualifiedSigned, opened, showPreparing, dispatch]);

  useEffect(() => {
    setShowRegistration(false);
    setAtrustNextAction('');
    setErrorOnRegistration('');
    setShowFormErrors(false);
    setAtrustRegistrationUrl('');
    setRegistrationStatusChecking(false);
    setSignRepeatNeeded(false);
    resetPreparingState();

    if (!opened) {
      return;
    }

    // Resume short-term Document Sign if Redux deadline still valid.
    const st = atrustShortTermRef.current;
    const reqId = recipientRequestIdRef.current;
    if (
      st &&
      st.recipientRequestId === reqId &&
      isAtrustShortTermWorkflow(st.activationWorkflow) &&
      st.deadlineAt > Date.now()
    ) {
      gae('ATRUST_REGISTER_POP_UP', 'RESUME_SHORT_TERM_DOCUMENT_SIGN');
      setPrepPurpose(PREP_PURPOSE.REGISTER);
      setStep(QES_STEP_ID.COMPLETE_REGISTRATION, QES_STEP_STATUS.DONE);
      showSigningAwaitCta();
    }
    // Resume only on open; showSigningAwaitCta is stable enough for this edge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const providerName = atrustProvider.displayName;

  const buildVisibleSteps = () => {
    const order = [
      QES_STEP_ID.CREATE_REQUEST,
      QES_STEP_ID.PREPARE_DOCUMENT,
      QES_STEP_ID.CHECK_REGISTRATION,
      QES_STEP_ID.COMPLETE_REGISTRATION,
      QES_STEP_ID.COMPLETE_SIGNING,
      QES_STEP_ID.SIGNED_SUCCESS
    ];
    const labels = {
      [QES_STEP_ID.CREATE_REQUEST]: 'qes_step_create_request',
      [QES_STEP_ID.PREPARE_DOCUMENT]: 'qes_step_prepare_document',
      [QES_STEP_ID.CHECK_REGISTRATION]: 'dialog_atrust_prep_check_registration',
      [QES_STEP_ID.COMPLETE_REGISTRATION]: registrationStatusChecking
        ? II18.get('dialog_atrust_prep_check_registration')
        : II18.get('dialog_atrust_complete_webid', providerName),
      [QES_STEP_ID.COMPLETE_SIGNING]: signRepeatNeeded
        ? II18.get('qes_step_check_signature')
        : II18.get('qes_step_complete_signing', providerName),
      [QES_STEP_ID.SIGNED_SUCCESS]: 'esignature_document_signed'
    };
    const registrationJustCompleted =
      stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.DONE;

    const shortTermSignHint = shortTermForRequest
      ? shortTermExpired
        ? 'dialog_atrust_short_term_expired'
        : 'dialog_atrust_short_term_done_sign_hint'
      : null;

    const shortTermGauge =
      shortTermForRequest && !shortTermExpired ? (
        <div
          id="atrust_short_term_timer"
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            marginBottom: 16
          }}
        >
          <Gauge
            type="circle"
            value={Math.max(
              0,
              Math.min(1, shortTermRemainingMs / ATRUST_SHORT_TERM_MS)
            )}
            size={96}
            bgColor="#fff"
            borderBgColor="#e8e8e8"
            borderColor="#0083e5"
            borderWidth={8}
            valueText={formatShortTermMmSs(shortTermRemainingMs)}
            valueFontSize={18}
            valueTextColor="#0083e5"
            labelText=""
          />
        </div>
      ) : null;

    const hints = {
      [QES_STEP_ID.CREATE_REQUEST]: 'qes_step_create_request_hint',
      [QES_STEP_ID.COMPLETE_REGISTRATION]: registrationStatusChecking
        ? 'dialog_atrust_status_check_hint'
        : atrustProvider.nopopupMessageKey,
      [QES_STEP_ID.COMPLETE_SIGNING]: signRepeatNeeded
        ? 'dialog_atrust_sign_status_check_hint'
        : registrationJustCompleted
        ? shortTermSignHint || 'dialog_atrust_registration_done_sign_hint'
        : atrustProvider.nopopupMessageKey
    };

    const createRequestAction = showCreateRequestCta ? (
      <Button
        id="atrust_retry_create_request"
        raised
        fill
        style={{ minWidth: 120 }}
        onClick={onRetryCreateRequest}
      >
        {II18.get('title_next')}
      </Button>
    ) : null;

    const ctaStepIds = new Set([
      QES_STEP_ID.COMPLETE_REGISTRATION,
      QES_STEP_ID.COMPLETE_SIGNING
    ]);

    const useSignCtaCooldown =
      registrationStatusChecking || Boolean(signRepeatNeeded);

    const stepActionButton = showCta ? (
      useSignCtaCooldown ? (
        <QesCooldownFillButton
          id="atrust_start_webid"
          cooling={restartCooldown}
          disabled={startingCta}
          durationMs={5000}
          style={{ minWidth: 120 }}
          onClick={onPrepStart}
        >
          {ctaLabel()}
        </QesCooldownFillButton>
      ) : (
        <Button
          id={'atrust_start_webid'}
          raised
          fill
          style={{ minWidth: 120 }}
          disabled={startingCta}
          onClick={onPrepStart}
        >
          {ctaLabel()}
        </Button>
      )
    ) : null;

    const stepAction =
      shortTermGauge || stepActionButton ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%'
          }}
        >
          {shortTermGauge}
          {stepActionButton}
        </div>
      ) : null;

    const prepCollapseIds = new Set([
      QES_STEP_ID.CREATE_REQUEST,
      QES_STEP_ID.PREPARE_DOCUMENT,
      QES_STEP_ID.CHECK_REGISTRATION,
      QES_STEP_ID.COMPLETE_REGISTRATION
    ]);

    const steps = order
      .filter(id => {
        const status = stepStatus[id];
        if (status == null || status === QES_STEP_STATUS.PENDING) return false;
        // Probe runs silently; only surface the step when an account was found.
        if (
          id === QES_STEP_ID.CHECK_REGISTRATION &&
          status !== QES_STEP_STATUS.DONE
        ) {
          return false;
        }
        if (registrationJustCompleted && prepCollapseIds.has(id)) {
          return false;
        }
        return true;
      })
      .map(id => ({
        id,
        label: labels[id],
        status: stepStatus[id],
        hint: hints[id],
        hintHelp:
          ctaStepIds.has(id) &&
          !registrationStatusChecking &&
          !signRepeatNeeded &&
          !(id === QES_STEP_ID.COMPLETE_SIGNING && registrationJustCompleted),
        framed:
          registrationJustCompleted &&
          id === QES_STEP_ID.COMPLETE_SIGNING &&
          stepStatus[id] !== QES_STEP_STATUS.DONE,
        action:
          id === QES_STEP_ID.CREATE_REQUEST
            ? createRequestAction
            : ctaStepIds.has(id)
            ? stepAction
            : undefined
      }));

    if (!registrationJustCompleted) {
      return steps;
    }

    return [
      {
        id: 'prep_summary',
        label: 'qes_step_prep_completed',
        status: QES_STEP_STATUS.DONE,
        hint: 'dialog_atrust_prep_completed_sub',
        hintWhenDone: true
      },
      ...steps
    ];
  };

  const ctaLabel = () => {
    if (registrationStatusChecking) {
      return II18.get('dialog_atrust_restart_webid');
    }
    const ss = stepStatus;
    const regAwait =
      ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.AWAITING_USER ||
      ss[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.BLOCKED;
    if (regAwait && prepPurpose === PREP_PURPOSE.REGISTER) {
      return II18.get('dialog_atrust_start_webid');
    }
    const signAwait =
      ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.AWAITING_USER ||
      ss[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.BLOCKED;
    if (shortTermForRequest && signAwait) {
      if (shortTermExpired) {
        return II18.get('dialog_atrust_restart_webid');
      }
      return II18.get(
        signRepeatNeeded
          ? 'dialog_atrust_repeat_document_sign'
          : 'dialog_atrust_document_sign'
      );
    }
    return II18.get('title_start');
  };

  const showCta =
    phase === 'preparing' &&
    (registrationStatusChecking ||
      (readyForPrepStart &&
        (stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
          QES_STEP_STATUS.AWAITING_USER ||
          stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
            QES_STEP_STATUS.BLOCKED ||
          stepStatus[QES_STEP_ID.COMPLETE_SIGNING] ===
            QES_STEP_STATUS.AWAITING_USER ||
          stepStatus[QES_STEP_ID.COMPLETE_SIGNING] ===
            QES_STEP_STATUS.BLOCKED)));

  const showCreateRequestCta =
    phase === 'preparing' &&
    !credentialsActive &&
    !embedCredentials &&
    stepStatus[QES_STEP_ID.CREATE_REQUEST] === QES_STEP_STATUS.AWAITING_USER;

  const onRetryCreateRequest = () => {
    gae('ATRUST_REGISTER_POP_UP', 'RETRY_CREATE_REQUEST');
    setStep(QES_STEP_ID.CREATE_REQUEST, QES_STEP_STATUS.RUNNING);
    setWaitForDocumentIsReadyToSign(true);
    prepareDocumentToSign();
  };

  const onCredentialsBack = () => {
    gae('ATRUST_REGISTER_POP_UP', 'CREDENTIALS_BACK');
    credsStepRef.current?.cancel();
    cancelPreparing();
  };

  const onCredentialsNext = () => {
    gae('ATRUST_REGISTER_POP_UP', 'CREDENTIALS_NEXT');
    credsStepRef.current?.submit();
  };

  // Async prep in flight — Back would leave create/finalise mid-flight.
  const prepBusy =
    startingCta ||
    embedCredentials ||
    stepStatus[QES_STEP_ID.CREATE_REQUEST] === QES_STEP_STATUS.RUNNING ||
    stepStatus[QES_STEP_ID.PREPARE_DOCUMENT] === QES_STEP_STATUS.RUNNING ||
    stepStatus[QES_STEP_ID.CHECK_REGISTRATION] === QES_STEP_STATUS.RUNNING;

  const preparingHelpKey = () => {
    if (embedCredentials) {
      return 'user_credentials_title';
    }
    if (showCreateRequestCta) {
      return 'qes_ready_create_request_help';
    }
    if (registrationStatusChecking) {
      return 'dialog_atrust_status_check_help';
    }
    if (signRepeatNeeded) {
      return 'dialog_atrust_sign_status_check_help';
    }
    if (showCta) {
      const regAwait =
        stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
          QES_STEP_STATUS.AWAITING_USER ||
        stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
          QES_STEP_STATUS.BLOCKED;
      if (regAwait && prepPurpose === PREP_PURPOSE.REGISTER) {
        return 'dialog_atrust_ready_for_webid_help';
      }
      // Short-term copy + timer live on the framed signing step hint only.
      if (shortTermForRequest) {
        return '';
      }
      return 'dialog_atrust_ready_to_sign_help';
    }
    // User already started — waiting on external provider window
    if (
      stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] === QES_STEP_STATUS.RUNNING
    ) {
      return 'dialog_atrust_awaiting_webid_help';
    }
    if (stepStatus[QES_STEP_ID.COMPLETE_SIGNING] === QES_STEP_STATUS.RUNNING) {
      return 'dialog_atrust_awaiting_sign_help';
    }
    if (prepPurpose === PREP_PURPOSE.LOGIN) {
      return 'dialog_atrust_preparing_help_login';
    }
    return 'dialog_atrust_preparing_help';
  };

  const doHandleClose = () => {
    gae('ATRUST_REGISTER_POP_UP', 'CLOSE');
    signWatchRef.current?.pause();
    stopCheckingRegistration();
    props.onClose();
  };

  const handleClose = () => {
    if (isRegistrationPollActive()) {
      confirmAbortStatusCheck(doHandleClose);
      return;
    }
    doHandleClose();
  };

  return (
    <Popup
      id={'ATRUST_REGISTER_POPUP'}
      className="ATRUST_REGISTER_POPUP common-dialog"
      animate={!noanimate}
      opened={Boolean(opened)}
      swipeToClose={false}
      closeByBackdropClick={false}
      closeByOutsideClick={false}
      onPopupClosed={() => {
        gae('ATRUST_REGISTER_POP_UP', 'CLOSE');
      }}
      onPopupOpen={() => gae('ATRUST_REGISTER_POP_UP', 'SHOW')}
    >
      <Page style={{ background: '#fff' }} className={'pageWithScroll'}>
        <Navbar title={II18.get('dialog_atrust_settings_title')}>
          <NavRight>
            <Link onClick={handleClose}>
              <II18>title_close</II18>
            </Link>
          </NavRight>
        </Navbar>

        {opened && (
          <>
            {showPreparing || phase === 'success' ? (
              <div
                id={'atrust_preparing'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: '40px 24px 24px',
                  height: '100%',
                  boxSizing: 'border-box'
                }}
              >
                {!embedCredentials && (
                  <img
                    src="/id_austria.png"
                    width={80}
                    alt="ID Austria"
                    style={{ opacity: 1 }}
                  />
                )}
                <h3
                  style={{
                    marginTop: embedCredentials ? 0 : 24,
                    marginBottom: 8
                  }}
                >
                  {phase === 'success' ? (
                    <II18>esignature_document_signed</II18>
                  ) : embedCredentials ? (
                    <II18>user_credentials_title</II18>
                  ) : (
                    <II18>dialog_atrust_preparing_title</II18>
                  )}
                </h3>
                {phase !== 'success' &&
                  !embedCredentials &&
                  preparingHelpKey() && (
                    <p
                      style={{
                        color: '#999',
                        fontSize: 14,
                        textAlign: 'center',
                        marginBottom: 24
                      }}
                    >
                      <II18>{preparingHelpKey()}</II18>
                    </p>
                  )}
                {embedCredentials ? (
                  <UserCredentialsStep
                    ref={credsStepRef}
                    active={embedCredentials}
                    onValidityChange={setCredsValid}
                  />
                ) : (
                  <QesStepList steps={buildVisibleSteps()} />
                )}
                {errorOnRegistration && phase === 'preparing' && (
                  <div
                    className="alert alert-danger"
                    role="alert"
                    style={{ marginTop: 16, width: '100%', maxWidth: 320 }}
                  >
                    {errorOnRegistration}
                  </div>
                )}
                {phase === 'success' ? (
                  <QesSuccessFooter
                    onDone={() => {
                      if (onSignFlowDone) onSignFlowDone();
                      else props.onClose();
                    }}
                  />
                ) : embedCredentials ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-around',
                      width: '100%',
                      marginTop: 'auto',
                      padding: 30,
                      paddingTop: 10
                    }}
                  >
                    <Button
                      id={'atrust_credentials_back'}
                      onClick={onCredentialsBack}
                    >
                      {II18.get('title_back')}
                    </Button>
                    <Button
                      id={'atrust_credentials_next'}
                      raised
                      fill
                      style={{ minWidth: 120 }}
                      disabled={!credsValid}
                      onClick={onCredentialsNext}
                    >
                      {userCredentialsTask?.customTitle ||
                        II18.get('title_next')}
                    </Button>
                  </div>
                ) : shortTermForRequest &&
                  stepStatus[QES_STEP_ID.COMPLETE_REGISTRATION] ===
                    QES_STEP_STATUS.DONE ? null : (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      marginTop: 'auto',
                      padding: 30,
                      paddingTop: 10
                    }}
                  >
                    <Button
                      id={'atrust_prep_back'}
                      onClick={cancelPreparing}
                      disabled={prepBusy}
                    >
                      {II18.get('title_back')}
                    </Button>
                  </div>
                )}
              </div>
            ) : showRegistration ? (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                <div
                  style={{
                    flexGrow: 1,
                    overflow: 'hidden',
                    overflowY: 'auto',
                    padding: 10,
                    margin: 10,
                    height: '100%'
                  }}
                  className={'tinny-scrollbar'}
                >
                  {errorOnRegistration && (
                    <div className="alert alert-danger" role="alert">
                      {errorOnRegistration}
                    </div>
                  )}
                  <RegistrationForm
                    signatureFieldId={signatureFieldId}
                    signatureFields={atrustRegisterFormStructure}
                    showFormErrors={showFormErrors}
                    currentRecipientData={currentRecipientData}
                  />
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-around',
                    padding: 30,
                    paddingTop: 10
                  }}
                >
                  <Button
                    id={'atrust_register_back'}
                    style={{ width: '100%' }}
                    onClick={() => {
                      gae('ATRUST_REGISTER_POP_UP', 'USER_BACK_TO_CHOICE');
                      setShowRegistration(false);
                      setShowFormErrors(false);
                      setErrorOnRegistration('');
                      setAtrustNextAction('');
                      setPhase('choice');
                    }}
                  >
                    {II18.get('title_back')}
                  </Button>
                  <Button
                    style={{ width: '100%' }}
                    onClick={beginRegistrationPrep}
                    raised
                    fill
                  >
                    {II18.get('title_next')}
                  </Button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'start',
                  alignItems: 'center',
                  paddingTop: 20
                }}
              >
                <img
                  src="/id_austria.png"
                  width={100}
                  alt="ID Austria"
                  style={{ opacity: 1 }}
                />
                <span style={{ color: '#999', fontSize: 14 }}>
                  <II18>dialog_atrust_select_next_options</II18>
                </span>
                <AtrustOption
                  id={'atrust_login_exists'}
                  title={II18.get('dialog_atrust_login_exists')}
                  desc={II18.get('dialog_atrust_login_exists_help')}
                  onClick={() => setAtrustNextAction(ATRUST_ACTIONS.LOGIN)}
                  icon={'material:input'}
                  selected={atrustNextAction === ATRUST_ACTIONS.LOGIN}
                />
                <AtrustOption
                  id={'atrust_register'}
                  title={II18.get('dialog_atrust_register')}
                  desc={II18.get('dialog_atrust_register_help')}
                  onClick={() => setAtrustNextAction(ATRUST_ACTIONS.REGISTER)}
                  icon={'f7:person_crop_square'}
                  selected={atrustNextAction === ATRUST_ACTIONS.REGISTER}
                />
                <Button
                  id={'atrust_next'}
                  style={{ marginTop: 30, width: 150 }}
                  raised
                  disabled={!atrustNextAction}
                  fill
                  onClick={onChoiceNext}
                >
                  <II18>title_next</II18>
                </Button>
              </div>
            )}
          </>
        )}
      </Page>
    </Popup>
  );
}

AtrustRegisterPopUp.propTypes = {
  noanimate: PropTypes.bool,
  opened: PropTypes.any,
  docId: PropTypes.string,
  onClose: PropTypes.func,
  signatureFieldId: PropTypes.string,
  documentIsReadyToSign: PropTypes.bool,
  prepareDocumentToSignError: PropTypes.bool,
  prepareDocumentToSign: PropTypes.func,
  currentRecipientData: PropTypes.object,
  templateMode: PropTypes.bool,
  documentIsQualifiedSigned: PropTypes.bool,
  onSignFlowDone: PropTypes.func
};
